import { AnchorHTMLAttributes, MouseEventHandler } from 'react';
import c from 'classnames';
import { Icon, IconName } from '../Icon';
import Typography, {
  TextColorType,
  TypographyComponentVariant,
} from '../Typography/Typography';
import styles from './TextLink.module.css';

export type LinkContentProps = {
  label: string;
  size?: TypographyComponentVariant;
  color?: TextColorType;
  iconNameLeft?: IconName;
  iconNameRight?: IconName;
  iconSize?: 's' | 'm';
  className?: string;
  role?: string;
  onClick?: MouseEventHandler;
};

export interface GenericLinkProps<
  TTo = string,
  TFrom = string,
> extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: TTo;
  from?: TFrom;
  state?: Record<string, unknown>;
  className?: string;
  replace?: boolean;
  preload?: false | 'viewport' | 'intent' | 'render';
  children?: React.ReactNode;
}

// A generic link component type that can vary `to` and `from` props
export type GenericLinkComponent<
  TTo = string,
  TFrom = string,
> = React.ComponentType<GenericLinkProps<TTo, TFrom>>;

type ExtractToProp<C extends GenericLinkComponent<any, any>> =
  React.ComponentProps<C>['to'];
type ExtractFromProp<C extends GenericLinkComponent<any, any>> =
  React.ComponentProps<C> extends { from?: infer F } ? F : never;

interface BaseTextLinkProps<
  C extends GenericLinkComponent<any, any> = GenericLinkComponent,
> extends LinkContentProps {
  external?: boolean;
  state?: Record<string, unknown>;
  from?: ExtractFromProp<C>;
  LinkComponent?: C;
  preload?: false | 'viewport' | 'intent' | 'render';
  linkContentClassName?: string; // For styling the inner LinkContent div
}

interface ToLinkProps<
  C extends GenericLinkComponent<string, string>,
> extends BaseTextLinkProps<C> {
  to: ExtractToProp<C>;
  onClick?: MouseEventHandler;
  contentOnly?: false;
  replace?: boolean;
}

interface WithOnClickOnly<
  C extends GenericLinkComponent<string, string>,
> extends BaseTextLinkProps<C> {
  onClick: MouseEventHandler;
  to?: undefined | '';
  contentOnly?: false;
  from?: undefined;
}

interface WithContentOnly<
  C extends GenericLinkComponent<string, string>,
> extends BaseTextLinkProps<C> {
  contentOnly: true;
  to?: undefined | '';
  onClick?: undefined;
  from?: undefined;
}

export type TextLinkProps<
  C extends GenericLinkComponent<string, string> = GenericLinkComponent,
> =
  | (ToLinkProps<C> &
      Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'to'>)
  | (WithOnClickOnly<C> &
      Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'to'>)
  | (WithContentOnly<C> &
      Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'to'>);

const LinkContent = (props: LinkContentProps): JSX.Element => {
  const {
    label,
    size = 'label',
    color,
    iconNameLeft,
    iconNameRight,
    iconSize = 's',
    onClick,
    className,
    role,
  } = props;
  const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    onClick?.(e);
  };

  return (
    <div
      className={c(styles.link, color && styles[color], className)}
      onClick={handleClick}
      role={role}
    >
      {iconNameLeft && <Icon name={iconNameLeft} size={iconSize} />}
      <Typography variant={size} color={color}>
        {label}
      </Typography>
      {iconNameRight && <Icon name={iconNameRight} size={iconSize} />}
    </div>
  );
};

export function TextLink<
  C extends GenericLinkComponent<any, any> = GenericLinkComponent,
>(props: TextLinkProps<C>): JSX.Element | null {
  const {
    label,
    external,
    onClick,
    className,
    linkContentClassName,
    state: navigationState,
    LinkComponent,
    ...rest
  } = props;

  const handleClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    onClick?.(e);
  };

  // Handle contentOnly scenario
  if ('contentOnly' in props && props.contentOnly) {
    return <LinkContent label={label} {...rest} className={className} />;
  }

  // Handle onClick-only scenario
  if ('onClick' in props && props.onClick && !('to' in props && props.to)) {
    return (
      <LinkContent
        label={label}
        {...rest}
        className={className}
        onClick={handleClick}
        role='button'
      />
    );
  }

  // Handle to scenario
  if ('to' in props && props.to) {
    const { to } = props;
    const from = 'from' in props ? props.from : undefined;
    const url = typeof to === 'string' ? to : to?.pathname;

    // If no LinkComponent, render as <a>
    if (!LinkComponent) {
      const isExternal =
        external || (typeof url === 'string' && url.startsWith('http'));
      return (
        <a
          href={typeof url === 'string' ? url : undefined}
          target={isExternal ? '_blank' : undefined}
          className={c(styles.link, className)}
          rel={isExternal ? 'noreferrer' : undefined}
          data-tracking-source='TextLink'
          role='link'
        >
          <LinkContent
            label={label}
            {...rest}
            onClick={handleClick}
            className={linkContentClassName}
          />
        </a>
      );
    } else {
      // Construct props for the LinkComponent
      const linkProps = {
        to: to as React.ComponentPropsWithoutRef<C>['to'],
        className: c(styles.link, className),
        role: 'link',
        ...(navigationState ? { state: navigationState } : {}),
        // Only include from if it's defined and not empty
        ...(from && from !== '' ? { from } : {}),
        target: external ? '_blank' : undefined,
        rel: external ? 'noreferrer' : undefined,
        replace: 'replace' in props ? props.replace : undefined,
        preload: 'preload' in props ? props.preload : undefined,
        ...rest,
      } as React.ComponentPropsWithoutRef<C>;

      // Remove undefined values to prevent prop type errors
      Object.keys(linkProps).forEach(key => {
        const k = key as keyof typeof linkProps;
        if (linkProps[k] === undefined) {
          delete linkProps[k];
        }
      });

      const linkComponentProps = linkProps as JSX.LibraryManagedAttributes<
        C,
        GenericLinkProps<string, string>
      >;

      return (
        <LinkComponent {...linkComponentProps}>
          <LinkContent label={label} {...rest} onClick={handleClick} />
        </LinkComponent>
      );
    }
  }

  // Should not reach here if types are correct
  return null;
}
